/**
 * i18n setup.  Import this once in main.tsx before rendering.
 *
 * Translations live in the `resources` object below.
 * To add a new language (e.g. Armenian) add an `hy` key alongside `en` with
 * the same shape and call `i18n.changeLanguage('hy')`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { extrasEn, extrasHy, extrasRu } from './locales/extra.js';
import { ruCore } from './locales/ru-core.js';
import { deepMerge, LANG_STORAGE_KEY, resolveAppLang } from './locale.js';

const en = {
  common: {
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    add: 'Add',
    create: 'Create',
    signOut: 'Sign out',
    signIn: 'Sign in',
    copy: 'Copy',
    retry: 'Retry',
    search: 'Search…',
    noData: 'No data yet.',
    required: 'required',
    optional: 'optional',
  },
  nav: {
    clients: 'Clients',
    dashboard: 'Dashboard',
    sales: 'Sales',
    menuInsights: 'Menu Insights',
    staffInsights: 'Staff Insights',
    staffComps: 'Staff comps',
    businessHealth: 'Business Health',
    inventoryInsights: 'Inventory',
    purchases: 'Purchases',
    orders: 'Orders',
    shifts: 'Shifts',
    timesheet: 'Timesheet',
    stockCount: 'Stock count',
    cashDrops: 'Cash drops',
    menu: 'Menu',
    modifiers: 'Modifiers',
    ingredients: 'Ingredients',
    recipes: 'Recipes',
    users: 'Users',
    cashiers: 'Cashiers',
    receipt: 'Receipt',
    customerDisplay: 'Customer display',
    invoices: 'Invoices',
    stockIn: 'Stock In',
    waste: 'Waste Log',
    prepBatches: 'Prep Batches',
    suppliers: 'Suppliers',
    staff: 'Staff',
    floorPlan: 'Floor plan',
    bookings: 'Bookings',
    bookingInsights: 'Booking insights',
    rooms: 'Bookings',
    settings: 'Settings',
    posDiagnostics: 'POS diagnostics',
    loyalty: 'Loyalty',
  },
  posDiagnostics: {
    title: 'POS diagnostics',
    hint: 'Snapshots submitted from POS terminals (orders CSV, outbox, app log).',
    empty: 'No diagnostic uploads yet.',
    download: 'Download',
  },
  auth: {
    username: 'Username',
    email: 'Email',
    password: 'Password',
    signingIn: 'Signing in…',
    noRoleTitle: 'No role assigned',
    noRoleBody: 'This account has no role yet. Ask a super admin to provision it.',
    notConfiguredTitle: 'Supabase not configured',
    resetPasswordTitle: 'Set new password',
    resetPasswordBody: 'Choose a new password for your account.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    updatePassword: 'Update password',
    passwordMismatch: 'Passwords do not match.',
    forgotPassword: 'Forgot password?',
    forgotPasswordTitle: 'Reset password',
    forgotPasswordBody: 'Enter your email and we will send a reset link.',
    sendResetLink: 'Send reset link',
    resetLinkSent: 'Check your email for a reset link.',
    backToSignIn: 'Back to sign in',
  },
  stockCount: {
    title: 'Stock count',
    subtitle: 'Walk the store and enter what you actually have on hand.',
    subtitleSingle: 'Search an ingredient, enter the counted qty, and save one at a time.',
    viewLossInsights: 'Loss & variance',
    system: 'System',
    counted: 'Counted',
    lineNote: 'Note (optional)',
    sessionNote: 'Session note (optional)',
    countedAt: 'Counted at',
    countedNow: 'Count time',
    changeTime: 'Change time',
    hideTime: 'Use now',
    post: 'Post count',
    saveOne: 'Save count',
    savedOne: '{{name}} counted and saved.',
    savedMatched: '{{name}} counted — matches system ✓',
    savedVariance: '{{name}} saved · {{delta}} {{unit}} vs system',
    matched: 'Matches system',
    recount: 'Re-count',
    allDone: 'All ingredients counted this session.',
    enterQty: 'Enter a counted quantity.',
    confirmTitle: 'Post stock count?',
    confirmBody: '{{counted}} items counted · {{variances}} variances will update stock.',
    progress: '{{done}} / {{total}} counted',
    varianceCount: '{{count}} variances',
    noResults: 'No ingredients match.',
    nothingCounted: 'Count at least one ingredient before posting.',
    invalidQty: 'Invalid quantity for {{name}}.',
    invalidTime: 'Invalid count time.',
    futureTime: 'Count time cannot be in the future.',
    filter: {
      all: 'All',
      remaining: 'Remaining',
      done: 'Done',
      uncounted: 'Remaining',
      variance: 'Variance',
      low: 'Low stock',
    },
    postedVariances: 'Posted variances',
    postedVariancesHint:
      'Recent count lines that changed stock. Revert a wrong count here — it stays in history but is excluded from loss metrics.',
    noPostedVariances: 'No posted variances yet.',
    revert: 'Revert',
    reverted: 'Reverted',
    revertSuccess: 'Count reverted — stock restored.',
    colWhen: 'When',
    colIngredient: 'Ingredient',
    colDelta: 'Delta',
    colDetail: 'Detail',
    sessionTeamCount: 'Team count in progress',
    sessionOpenedBy: 'Started by {{name}} · {{when}}',
    sessionSomeone: 'Someone',
    sessionClose: 'Close session',
    sessionClosedSuccess: 'Count session closed.',
    sessionNoActive: 'No active count session',
    sessionNoActiveHint:
      'Start a session so your team can see what is counted and what is left. Stock still updates instantly on each save.',
    sessionStart: 'Start session',
    sessionLive: 'Live updates from your team',
    sessionCountedBy: 'Counted by {{name}}',
  },
  wasteLog: {
    title: 'Log waste',
    subtitle: 'Record discarded ingredients. Reports are in Inventory insights.',
    viewInsights: 'Waste insights',
    viewAll: 'View all waste',
    ingredient: 'Ingredient',
    ingredientPlaceholder: 'Search ingredient…',
    stock: 'Stock',
    qty: 'Quantity',
    reason: 'Reason',
    note: 'Note',
    notePlaceholder: 'Optional details',
    submit: 'Log waste',
    recent: 'Recent',
    none: 'No waste logged yet.',
    pickIngredient: 'Select an ingredient.',
    positiveQty: 'Enter a positive quantity.',
  },
  cashiers: {
    title: 'Cashiers',
    subtitle: 'Create POS logins for your staff.',
    usernamePlaceholder: 'Username (login name)',
    fullNamePlaceholder: 'Full name',
    hdmNo: 'HDM cashier no',
    create: 'Create cashier',
    credsTitle: 'Cashier credentials',
    credsNote: 'Copy and send these to the cashier. Shown only once.',
  },
  menu: {
    title: 'Menu',
    categories: 'Categories',
    products: 'Products',
    addCategory: 'Add category',
    addProduct: 'Add product',
    importCsv: 'Import CSV',
    priceList: 'Price list',
    allCategories: 'All',
    print: 'Print',
    openRecipe: 'Open recipe',
  },
  ingredients: {
    title: 'Ingredients',
    addIngredient: 'Add ingredient',
    importCsv: 'Import CSV',
    restock: 'Restock',
    lowStock: 'Low stock',
    colName: 'Name',
    colUnit: 'Unit',
    colStock: 'Stock',
    colCost: 'Cost price',
    noIngredients: 'No ingredients yet.',
    merge: 'Merge ingredients',
    mergeTitle: 'Merge duplicate ingredients',
    mergeDescription:
      'Combine two regular ingredients into one. References move to the kept row; stock and cost are combined. Prep ingredients cannot be merged.',
    mergeKeep: 'Keep (survivor)',
    mergeRemove: 'Merge away (delete)',
    mergeSelectKeep: 'Select ingredient to keep…',
    mergeSelectRemove: 'Select duplicate to merge…',
    mergePreview: 'Preview',
    mergeRecipes: 'Recipe lines affected',
    mergeWillCombine: 'qty combined on same recipe',
    mergeWillRepoint: 'repointed',
    mergeSubstitutes: 'Substitute options',
    mergeChoiceOptions: 'Choice options',
    mergePurchaseLines: 'Purchase order lines',
    mergeTransactions: 'Inventory transactions',
    mergeCombinedStock: 'Combined stock',
    mergeCombinedCost: 'Combined unit cost',
    mergeUnitMismatch: 'Units must match to merge.',
    mergePrepBlocked: 'Prep ingredients cannot be merged.',
    mergeConfirm: 'Merge ingredients',
    mergeSuccess: 'Merged successfully.',
    recipes: 'Recipes',
    recipesUsing: 'Recipes using {{name}}',
    recipesUsingCaption: 'Open a recipe to edit it.',
    recipesNone: 'Not used in any recipe.',
  },
  recipes: {
    title: 'Recipes',
    description: 'Define recipes that link menu items to ingredients for automatic stock tracking.',
    createCard: 'Create recipe',
    addIngredient: 'Add ingredient',
    newCardName: 'New recipe name',
    selectHint: 'Select a recipe on the left to edit its ingredients.',
    item: 'ingredient',
    items: 'ingredients',
    noCards: 'No recipes yet. Create one above.',
    colIngredient: 'Ingredient',
    colSubstitute: 'Substitute with',
    colQty: 'Qty per portion',
    colCost: 'Cost',
    colPriceDelta: 'Price delta',
    totalCost: 'Total cost',
    costMissing: 'Missing unit cost on one or more ingredients',
    noSubstitute: 'No substitute',
    substituteOptional: 'optional',
    substituteQtyPlaceholder: 'Sub. qty',
    noItems: 'No ingredients yet — add one above.',
    prepStock: 'prep stock',
    prepStockOnSale: 'Depletes on sale',
    batchEquivalent: 'Batch equivalent',
    legacyPrepSubRecipe: 'Legacy sub-recipe — use prep ingredient instead',
    prepSubRecipeBlocked:
      'Prep recipes cannot be sub-recipes. Add the prep ingredient from prep batches instead.',
    prepStandardBatch: 'Standard batch cost',
    prepStandardUnit: 'Standard unit cost',
    prepStoredCost: 'Stored on ingredient',
    prepCostMissing: 'Missing raw cost for',
    prepCostSubRecipes: 'Sub-recipes are excluded from cost — use raw ingredients in the prep recipe.',
    recalcPrepCost: 'Recalculate standard cost',
  },
  sales: {
    title: 'Sales',
    forcedOnly: 'Show only force-closed orders',
    noOrders: 'No orders yet.',
    colNumber: '#',
    colTime: 'Time',
    colStatus: 'Status',
    colTotal: 'Total',
    colForced: 'Force closed',
    colNote: 'Note',
  },
  receiptTemplate: {
    title: 'Receipt template',
    subtitle: 'Customise the pre-payment receipt printed at the counter.',
    cafeName: 'Cafe name',
    header: 'Header',
    headerPlaceholder: 'Extra lines above the items (optional)',
    footer: 'Footer',
    footerPlaceholder: 'Closing message below the total',
    paperWidth: 'Paper width',
    printFontSize: 'Print font size',
    fontNormal: 'Normal',
    fontLarge: 'Large (double height)',
    fontXlarge: 'Extra large (double width + height)',
    printFontSizeHint: 'Uses ESC/POS commands on the thermal printer. Extra large fits fewer characters per line.',
    printBold: 'Bold text',
    printBoldHint: 'Bold cafe name, items, and total on the printed receipt.',
    preview: 'Preview',
    save: 'Save template',
    saved: 'Saved',
  },
  customerDisplay: {
    title: 'Customer display',
    subtitle: 'Configure the tablet facing the customer — idle banner and card tips.',
    enabled: 'Enable customer display',
    enabledHint: 'POS serves a page on port 9200; open http://<pos-ip>:9200 on the tablet.',
    bannerTitle: 'Banner title',
    bannerMessage: 'Banner message',
    bannerImageUrls: 'Banner images (up to 4)',
    bannerImageUrlsHint: 'Upload images or paste URLs. Shown on the idle screen, rotating every 5 seconds when more than one is set.',
    bannerImageUrlN: 'Image {{n}}',
    bannerImageUpload: 'Upload image',
    bannerImageUploading: 'Uploading…',
    bannerImageClear: 'Remove',
    bannerImageUrlPlaceholder: 'Or paste image URL…',
    tipsSection: 'Card tips',
    tipEnabled: 'Prompt for tip on card payments',
    percentPresets: 'Percentage presets (%)',
    smallOrderThreshold: 'Small-order threshold (֏)',
    smallOrderThresholdHint: 'Below this total, show fixed ֏ amounts instead of percentages.',
    fixedPresets: 'Fixed presets for small orders (֏)',
    tipTimeout: 'Tip timeout (seconds)',
    tipTimeoutHint: 'Customer has this long to choose; cashier can skip sooner.',
    fiscalSection: 'HDM tip line (fiscal)',
    fiscalHint: 'Required to print tips on the fiscal receipt. Leave department/ADG empty to skip fiscal tip line.',
    tipDepartmentId: 'Tip department ID',
    tipAdgCode: 'Tip ADG code',
    tipProductCode: 'Tip product code',
    tipProductName: 'Tip product name',
    save: 'Save settings',
    saved: 'Saved',
    loyaltySection: 'Loyalty signup promotion',
    loyaltySectionHint:
      'Show a “Join rewards” button on the idle customer screen. Guests tap it to see a QR code they can scan to register. Requires a signup link from Loyalty → Signup links.',
    loyaltyEnabled: 'Show loyalty signup on customer display',
    loyaltySignupUrl: 'Signup link URL',
    loyaltySignupUrlHint:
      'Paste the public join link (from Loyalty → copy link). The QR encodes this URL. Required to enable the promo.',
    loyaltyUrlRequired: 'Enter a signup link URL before enabling loyalty promotion.',
    loyaltyUrlRequiredHint: 'Add a signup link below — the toggle stays off until a URL is set.',
    loyaltyQuickLinks: 'Quick fill from signup links',
    loyaltyHeadline: 'QR screen headline',
    loyaltySubtext: 'QR screen subtext (benefit line)',
    loyaltyCtaLabel: 'Idle screen button label',
  },
  suppliers: {
    title: 'Suppliers',
    subtitle: 'Manage your suppliers. Legal names are populated automatically from approved e-invoices.',
    addSupplier: 'Add supplier',
    editSupplier: 'Edit supplier',
    noSuppliers: 'No suppliers yet.',
    noMatch: 'No suppliers match your search.',
    nameRequired: 'Supplier name is required.',
    tinRequired: 'TIN is required.',
    colName: 'Name',
    colLegalName: 'Legal name',
    colTin: 'TIN',
    colCategory: 'Category',
    colContact: 'Contact',
    colPhone: 'Phone',
    name: 'Supplier name',
    namePlaceholder: 'e.g. Fresh Market',
    legalName: 'Legal name',
    legalNameHint: 'from e-invoice',
    tin: 'TIN',
    tinLocked: 'cannot be changed',
    category: 'Category',
    contactPerson: 'Contact person',
    contactPersonPlaceholder: 'e.g. Armen Hakobyan',
    phone: 'Phone',
    cat_vegetables: 'Vegetables',
    cat_wine: 'Wine & alcohol',
    cat_milk: 'Milk & Dairy',
    cat_drinks: 'Drinks',
    cat_coffee: 'Coffee',
    cat_food: 'Food',
    cat_services: 'Services',
    cat_cleaning: 'Cleaning',
    cat_packaging: 'Packaging / disposables',
    cat_other: 'Other',
  },
  floorPlan: {
    title: 'Floor plan',
    subtitle:
      'Lay out rooms and tables for table service. The POS shows this exact layout on its Tables screen.',
    addTable: 'Add table',
    addRoom: 'Add room',
    roomNamePrompt: 'Room name (e.g. Terrace):',
    emptyRoom: 'No tables in this room yet — press “+ Add table”.',
    selectHint: 'Select a table on the canvas to edit it. Drag tables to arrange the floor.',
    tableName: 'Table name',
    seats: 'Seats',
    shape: 'Shape',
    shape_square: 'Square',
    shape_round: 'Round',
    shape_rect: 'Rect',
    size: 'Size',
    room: 'Room',
    active: 'Active',
    deactivatedInstead: 'Table has past orders, so it was deactivated instead of deleted.',
  },
  rooms: {
    title: 'Rooms',
    subtitle: 'Bookable rooms and their guest capacity. Each room has its own iCal feed URL.',
    add: 'Add room',
    edit: 'Edit room',
    empty: 'No rooms yet — add your first room to start taking bookings.',
    nameRequired: 'Room name is required.',
    priceRequired: 'Nightly price must be greater than zero.',
    namePlaceholder: 'e.g. Room 1, Garden Suite',
    colName: 'Name',
    colCapacity: 'Capacity',
    colPrice: 'Price / night',
    colSort: 'Sort order',
    colActive: 'Active',
    colFeed: 'iCal feed',
    activeYes: 'Yes',
    activeNo: 'No',
    copyFeed: 'Copy link',
    copied: 'Copied ✓',
    hdmTitle: 'HDM fiscal department',
    hdmHint:
      'Department id used when the POS prints a fiscal receipt for a room booking. Must match a department programmed on your HDM device (same as menu category “HDM dept”).',
    hdmDept: 'HDM dept',
    hdmDeptTitle: 'HDM fiscal department id (must match your device)',
    feedTitle: 'iCal availability export',
    feedHint:
      'Paste a room’s feed link into Airbnb, Booking.com or Expedia (“Import calendar”) to share its availability. Links contain a secret token — treat them like passwords.',
    rotate: 'Regenerate feed links',
    rotateConfirm: 'All previously shared feed links will stop working. Continue?',
    rotateYes: 'Yes, regenerate',
    widgetTitle: 'Booking widget',
    widgetHint:
      'A public availability calendar where guests can book directly. Share the link or embed it on your website with the iframe snippet.',
    widgetTitleLabel: 'Widget title',
    widgetTitlePlaceholder: 'Defaults to your business name',
    widgetColorLabel: 'Button color',
    widgetColorHint: 'Used for Reserve and Confirm on the public widget.',
    widgetOpen: 'Open widget',
    widgetCopyLink: 'Copy link',
    widgetCopyEmbed: 'Copy embed code',
    notifyEmailsTitle: 'Notify the cafe of new requests',
    notifyEmails: 'Cafe email list',
    notifyEmailsPlaceholder: 'cafe@example.com, manager@example.com',
    notifyEmailsHint:
      'Staff at these addresses are emailed when a guest sends a new booking request from the widget. The guest still receives their own request email.',
    photos: 'Photos',
    addPhoto: 'Add photo',
    photosHint: 'Shown in the booking widget. JPEG, PNG or WebP, up to 4 MB each.',
  },
  bookingWidget: {
    kicker: 'Reserve a stay',
    subtitle: 'Room availability',
    guests_one: '{{count}} guest',
    guests_other: '{{count}} guests',
    guestsLabel: 'Guests',
    perNight: '/ night',
    forStay: 'for {{count}} nights',
    fullyBooked: 'Fully booked',
    noRooms: 'No rooms available yet.',
    loadError: 'Could not load availability. Please try again later.',
    pickCheckin: 'Select your check-in date.',
    pickCheckout: 'Now choose when you leave.',
    nights_one: '{{count}} night',
    nights_other: '{{count}} nights',
    guestName: 'Full name',
    phone: 'Phone number',
    book: 'Book',
    email: 'Email',
    bookSuccessTitle: 'Request received',
    bookSuccess: 'We’ll email you when the property confirms or declines. This is not a confirmed reservation yet.',
    colRoom: 'Room',
    bookConflict: 'Those dates were just taken. Please pick another stay.',
    bookError: 'Something went wrong. Please try again.',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    addDate: 'Add date',
    resetDates: 'Reset',
    chooseDates: 'Choose dates',
    reserve: 'Reserve',
    unavailable: 'Unavailable',
    tooSmall: 'This room sleeps fewer than {{count}} guests.',
    tooSmallBadge: 'Too few beds',
    availableCount: '{{count}} rooms available',
    unavailableCount: '{{count}} unavailable',
    noMatch: 'Nothing free for those dates. Try a different stay.',
    browseHint: 'Pick dates to see what’s free — or browse the rooms below.',
    stayTotal: 'Total',
    bookAnother: 'Book another stay',
    back: 'All rooms',
    guestDetails: 'Your details',
    payOnSite: 'Pay at the property. No charge now.',
    confirmBooking: 'Send request',
    sleeps: 'Up to {{count}} guests',
    prevPhoto: 'Previous photo',
    nextPhoto: 'Next photo',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    fewerGuests: 'Fewer guests',
    moreGuests: 'More guests',
    datesNoLongerAvailable: 'That room is no longer free for the new dates. Please pick another.',
    done: 'Done',
    viewPhotos: 'View photos',
    closePhotos: 'Close photos',
    photoOf: '{{current}} / {{total}}',
  },
  bookings: {
    title: 'Bookings',
    subtitle: 'Room availability calendar. Click a free day to book, a booking to edit.',
    subtitleTable: 'All bookings for the selected month. Click a row to view or edit.',
    viewCalendar: 'Calendar',
    viewTable: 'Table',
    add: 'New booking',
    edit: 'Booking',
    today: 'Today',
    colRoom: 'Room',
    colNights: 'Nights',
    colStatus: 'Status',
    statusConfirmed: 'Confirmed',
    statusPending: 'Pending',
    statusRejected: 'Rejected',
    guestEmail: 'Email',
    emailInvalid: 'Enter a valid email address.',
    requestBanner: 'Website request — dates stay free until you confirm.',
    confirmRequest: 'Confirm request',
    rejectRequest: 'Reject',
    confirmRequestAsk: 'Confirm this request?',
    rejectRequestAsk: 'Reject this request?',
    confirmRequestYes: 'Yes, confirm',
    rejectRequestYes: 'Yes, reject',
    reviewError: 'Could not update this request. Try again.',
    noBookings: 'No bookings this month.',
    noRooms: 'No active rooms — add rooms first (Management → Bookings).',
    clickToBook: 'Click to create a booking',
    guestName: 'Guest name',
    phone: 'Phone',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    nights: '{{count}} night(s)',
    adults: 'Adults',
    kids: 'Kids',
    sourceLabel: 'Source',
    note: 'Note',
    roomRequired: 'Pick a room.',
    nameRequired: 'Guest name is required.',
    phoneRequired: 'Phone number is required.',
    datesInvalid: 'Check-out must be after check-in.',
    adultsRequired: 'At least one adult is required.',
    overCapacity: 'Too many guests for this room (capacity {{capacity}}).',
    conflict: 'This room is already booked for those dates.',
    cancelBooking: 'Cancel booking',
    cancelYes: 'Yes, cancel it',
    cancelled: 'Cancelled',
    close: 'Close',
    amount: 'Amount (AMD)',
    amountHint: 'Defaults from room price × nights; you can override.',
    amountRequired: 'Amount must be greater than zero.',
    paymentOption: 'Payment option',
    paymentOptionRequired: 'Payment option is required.',
    paymentOptionValue: {
      on_site: 'On-site',
      online: 'Online',
      free: 'Free',
    },
    freeHint: 'Complimentary stay. The room is occupied and nothing is charged.',
    freeAfterPayment: 'This stay already has a payment. Refund it before marking it free.',
    paymentStatusLabel: 'Payment status',
    paymentStatus: {
      unpaid: 'Unpaid',
      pending: 'Pending',
      paid: 'Paid',
      failed: 'Failed',
    },
    tender: {
      cash: 'Cash',
      card: 'Card',
    },
    fiscalTitle: 'Fiscal receipt',
    fiscalNumber: 'Fiscal #',
    fiscalTotal: 'Fiscal total',
    fiscalCrn: 'CRN',
    fiscalTin: 'TIN',
    fiscalTime: 'Printed at',
    fiscalNone: 'No fiscal receipt yet.',
    refund: 'Refund',
    refundBooking: 'Refund booking',
    refundTitle: 'Refund booking',
    refundConfirmCancel: 'The booking will be cancelled after the fiscal return receipt is issued.',
    refundReturnCash: 'Return {{amount}} in cash',
    refundReturnCard: 'Return {{amount}} to card',
    refunding: 'Issuing return receipt…',
    refundSuccess: 'Refunded — fiscal #{{fiscal}}. Booking cancelled.',
    refundFailed: 'Refund failed: {{msg}}',
    refundFiscalTitle: 'Return receipt',
    refundOnPosOnly:
      'Paid with a fiscal receipt — refund from the POS to issue a return receipt and cancel the booking.',
    payConfirmTitle: 'Confirm payment',
    payConfirmCash: 'Charge {{amount}} in cash and print a fiscal receipt?',
    payConfirmCard: 'Charge {{amount}} to card and print a fiscal receipt?',
    payConfirmYes: 'Confirm payment',
    source: {
      airbnb: 'Airbnb',
      expedia: 'Expedia',
      booking: 'Booking.com',
      web: 'Website',
      instagram: 'Instagram',
      on_site: 'On-site',
      other: 'Other',
    },
  },
  bookingInsights: {
    title: 'Booking insights',
    subtitle: 'Occupancy, ADR, RevPAR and channel performance for stays in the selected period.',
    period_7d: '7 days',
    period_30d: '30 days',
    period_90d: '90 days',
    kpiOccupancy: 'Occupancy',
    kpiAdr: 'ADR',
    kpiRevpar: 'RevPAR',
    kpiRevenue: 'Paid revenue',
    kpiStays: 'Confirmed stays',
    kpiAvgLos: 'Avg length of stay',
    kpiCollection: 'Collection rate',
    kpiOutstanding: 'Outstanding',
    kpiComplimentary: 'Complimentary',
    kpiCancelRate: 'Cancellation rate',
    compHint: 'Occupied nights excluded from revenue and ADR',
    adrHint: 'Paid revenue ÷ paid room-nights',
    revparHint: 'Paid revenue ÷ available room-nights',
    revenueHint: 'Collected in period',
    staysHint: 'Non-cancelled check-ins',
    cancelledCount: '{{count}} cancelled',
    roomNights: 'room-nights',
    nightsCount: '{{count}} nights',
    nightsShort: 'nights',
    staysShort: 'stays',
    trendTitle: 'Daily collected revenue',
    channelMixTitle: 'Revenue by channel',
    roomPerformanceTitle: 'Room performance',
    paymentMixTitle: 'Payment status',
    bySource: 'By source',
    byPaymentOption: 'By payment option',
    byRoom: 'By room',
    recentPaid: 'Recent settlements',
    colSource: 'Source',
    colOption: 'Payment option',
    colRoom: 'Room',
    colBookings: 'Bookings',
    colPaid: 'Paid',
    colRevenue: 'Revenue',
    colGuest: 'Guest',
    colDates: 'Dates',
    colAmount: 'Amount',
    colFiscal: 'Fiscal #',
    colTender: 'Tender',
    noData: 'No stays overlap this period.',
    noPaidBySource: 'No paid revenue by channel yet.',
    noPaidYet: 'No paid bookings in this period.',
  },
  staffComps: {
    title: 'Staff comps',
    subtitle: 'Free force-closes and staff meals logged as waste — by recipient and type.',
    period_7d: '7 days',
    period_30d: '30 days',
    period_90d: '90 days',
    kpiOrders: 'Comp orders',
    kpiTotal: 'Total value',
    kpiAvg: 'Avg per order',
    byRecipient: 'By recipient',
    byType: 'By comp type',
    colRecipient: 'Recipient',
    colType: 'Type',
    colOrders: 'Orders',
    colTotal: 'Total',
    noData: 'No structured comps in this period.',
    unassigned: 'Unassigned',
    unknownRecipient: 'Unknown',
  },
  compTypes: {
    staff_meal: 'Staff meal',
    owner: 'Owner',
    guest: 'Guest',
    training: 'Training',
    marketing: 'Marketing',
    waste: 'Waste',
    other: 'Other',
  },
  demo: {
    tryDemo: 'Try the live demo',
    or: 'or',
    signInHint: 'Shared demo — data resets nightly. Explore freely.',
    banner: 'Demo environment — sample data resets nightly. Changes you make are not permanent.',
  },
};

export type TranslationShape = typeof en;

const hy: TranslationShape = {
  common: {
    loading: 'Բեռնում…',
    save: 'Պահել',
    cancel: 'Չեղարկել',
    delete: 'Ջնջել',
    add: 'Ավելացնել',
    create: 'Ստեղծել',
    signOut: 'Ելք',
    signIn: 'Մուտք',
    copy: 'Պատճենել',
    retry: 'Կրկնել',
    search: 'Որոնել…',
    noData: 'Տվյալ չկա։',
    required: 'պարտադիր',
    optional: 'ոչ պարտադիր',
  },
  nav: {
    clients: 'Հաճախորդներ',
    dashboard: 'Վահանակ',
    sales: 'Վաճառք',
    menuInsights: 'Մենյու վերլ.',
    staffInsights: 'Անձնակ. վերլ.',
    staffComps: 'Անվճ. պատվ.',
    businessHealth: 'Բիզնես',
    inventoryInsights: 'Պաշարներ',
    purchases: 'Գնումներ',
    orders: 'Պատվերներ',
    shifts: 'Հերթեր',
    timesheet: 'Տաբել',
    stockCount: 'Հաշվ.',
    cashDrops: 'Կանխիկ',
    menu: 'Մենյու',
    modifiers: 'Հավելումներ',
    ingredients: 'Բաղադրիչներ',
    recipes: 'Բաղ.',
    users: 'Օգտ.',
    cashiers: 'Դրամարկղ.',
    receipt: 'Կտրոն',
    customerDisplay: 'Հաճ. էկրան',
    invoices: 'Հաշիվ-ապ.',
    stockIn: 'Ստ. ընդ.',
    waste: 'Թափոն',
    prepBatches: 'Պատրաստ.',
    suppliers: 'Մատակ.',
    staff: 'Աշխատակիցներ',
    floorPlan: 'Հատակագիծ',
    bookings: 'Ամրագրումներ',
    bookingInsights: 'Ամր. վերլուծություն',
    rooms: 'Ամրագրումներ',
    settings: 'Կարգավորումներ',
    posDiagnostics: 'POS ախտորոշում',
    loyalty: 'Լոյալություն',
  },
  posDiagnostics: {
    title: 'POS ախտորոշում',
    hint: 'POS-ից վերբեռնված snapshot-ներ (պատվերներ, outbox, log)։',
    empty: 'Դեռ վերբեռնումներ չկան։',
    download: 'Ներբեռնել',
  },
  auth: {
    username: 'Մուտքանուն',
    email: 'Էլ. փոստ',
    password: 'Գաղտնաբառ',
    signingIn: 'Մուտք…',
    noRoleTitle: 'Դեր չի նշանակված',
    noRoleBody: 'Այս հաշիվը դեռ դեր չունի։ Դիմեք ադմինիստրատորին։',
    notConfiguredTitle: 'Supabase-ը կարգավորված չէ',
    resetPasswordTitle: 'Նոր գաղտնաբառ',
    resetPasswordBody: 'Ընտրեք նոր գաղտնաբառ ձեր հաշվի համար։',
    newPassword: 'Նոր գաղտնաբառ',
    confirmPassword: 'Կրկնել գաղտնաբառը',
    updatePassword: 'Պահպանել',
    passwordMismatch: 'Գաղտնաբառերը չեն համընկնում։',
    forgotPassword: 'Մոռացե՞լ եք գաղտնաբառը',
    forgotPasswordTitle: 'Գաղտնաբառի վերականգնում',
    forgotPasswordBody: 'Մուտքագրեք էլ. փոստը — կուղարկենք վերականգնման հղում։',
    sendResetLink: 'Ուղարկել հղում',
    resetLinkSent: 'Ստուգեք էլ. փոստը վերականգնման հղման համար։',
    backToSignIn: 'Վերադառնալ մուտք',
  },
  stockCount: {
    title: 'Պաշարի հաշվ.',
    subtitle: 'Մուտքագրեք իրական մնացորդը.',
    subtitleSingle: 'Գտեք բաղադրիչը, մուտքագրեք քանակը և պահպանեք մեկ-մեկ.',
    viewLossInsights: 'Կորուստ և տարբ.',
    system: 'Համակ.',
    counted: 'Հաշվ.',
    lineNote: 'Նշում',
    sessionNote: 'Նշում',
    countedAt: 'Ժամանակ',
    countedNow: 'Հիմա',
    changeTime: 'Փոխել',
    hideTime: 'Հիմա',
    post: 'Հաստատել',
    saveOne: 'Պահպանել',
    savedOne: '{{name}} — պահպանված է.',
    savedMatched: '{{name}} — համընկնում է համակ. ✓',
    savedVariance: '{{name}} · {{delta}} {{unit}}',
    matched: 'Համընկնում է',
    recount: 'Կրկնել',
    allDone: 'Բոլոր բաղադրիչները հաշվարկված են.',
    enterQty: 'Մուտքագրեք քանակը.',
    confirmTitle: 'Հաստատե՞լ',
    confirmBody: '{{counted}} հաշվ. · {{variances}} տարբ.',
    progress: '{{done}} / {{total}}',
    varianceCount: '{{count}} տարբ.',
    noResults: 'Չկա',
    nothingCounted: 'Հաշվեք առնվազն մեկը',
    invalidQty: 'Սխալ քանակ',
    invalidTime: 'Սխալ ժամ',
    futureTime: 'Ապագա ժամ չի թույլատրվում',
    filter: { all: 'Բոլ.', remaining: 'Մնաց.', done: 'Պատր.', uncounted: 'Մնաց.', variance: 'Տարբ.', low: 'Քիչ' },
    postedVariances: 'Հաստատված տարբերություններ',
    postedVariancesHint:
      'Պաշար փոխած վերջին հաշվարկներ։ Սխալ հաշվարկը հետարկել այստեղ — պատմության մեջ մնում է, բայց կորուստից հանվում է։',
    noPostedVariances: 'Դեռ հաստատված տարբերություն չկա',
    revert: 'Հետարկել',
    reverted: 'Հետարկված',
    revertSuccess: 'Հաշվարկը հետարկված է — պաշարը վերականգնված',
    colWhen: 'Ժամ',
    colIngredient: 'Բաղադրիչ',
    colDelta: 'Տարբ.',
    colDetail: 'Մանր.',
    sessionTeamCount: 'Թիմային հաշվ.',
    sessionOpenedBy: '{{name}} · {{when}}',
    sessionSomeone: '—',
    sessionClose: 'Փակել',
    sessionClosedSuccess: 'Հաշվարկի սեսիան փակված է',
    sessionNoActive: 'Ակտիվ սեսիա չկա',
    sessionNoActiveHint: 'Սկսեք սեսիա — թիմը կտեսնի հաշվարկվածն ու մնացածը',
    sessionStart: 'Սկսել',
    sessionLive: 'Կենդանի թարմացում',
    sessionCountedBy: '{{name}}',
  },
  wasteLog: {
    title: 'Թափոն',
    subtitle: 'Գրանցեք թափոնը։ Հաշվետվություն՝ Պաշարներ.',
    viewInsights: 'Վերլույթ',
    viewAll: 'Բոլորը',
    ingredient: 'Բաղ.',
    ingredientPlaceholder: 'Որոնել…',
    stock: 'Պաշ.',
    qty: 'Քանակ',
    reason: 'Պատճ.',
    note: 'Նշում',
    notePlaceholder: 'Լրացուցիչ',
    submit: 'Գրանցել',
    recent: 'Վերջին',
    none: 'Դեռ չկա',
    pickIngredient: 'Ընտրեք բաղ.',
    positiveQty: 'Դրական քանակ',
  },
  cashiers: {
    title: 'Դրամարկղավարներ',
    subtitle: 'Ստեղծեք POS մուտքեր աշխատակիցների համար։',
    usernamePlaceholder: 'Մուտքանուն',
    fullNamePlaceholder: 'Անուն ազգանուն',
    hdmNo: 'HDM կոդ',
    create: 'Ստեղծել',
    credsTitle: 'Մուտքի տվյալներ',
    credsNote: 'Ուղարկեք դրամարկղավարին։ Ցուցադրվում է մեկ անգամ։',
  },
  menu: {
    title: 'Մենյու',
    categories: 'Կատեգորիաներ',
    products: 'Ապրանքներ',
    addCategory: 'Ավելացնել կատ.',
    addProduct: 'Ավելացնել ապ.',
    importCsv: 'CSV ներմուծում',
    priceList: 'Գնացուցակ',
    allCategories: 'Բոլորը',
    print: 'Տպել',
    openRecipe: 'Բացել բաղ.',
  },
  ingredients: {
    title: 'Բաղադրիչներ',
    addIngredient: 'Ավելացնել',
    importCsv: 'CSV ներմուծում',
    restock: 'Ավելացնել',
    lowStock: 'Քիչ',
    colName: 'Անուն',
    colUnit: 'Չ.մ.',
    colStock: 'Պաշ.',
    colCost: 'Ինքնար.',
    noIngredients: 'Բաղ. չկա։',
    merge: 'Միավորել',
    mergeTitle: 'Կրկնօրինակ բաղ. միավորում',
    mergeDescription:
      'Երկու սովորական բաղադրիչ միավորել մեկում։ Prep բաղադրիչները չեն միավորվում։',
    mergeKeep: 'Պահել',
    mergeRemove: 'Միավորել (ջնջել)',
    mergeSelectKeep: 'Ընտրել պահելի…',
    mergeSelectRemove: 'Ընտրել կրկնօր…',
    mergePreview: 'Նախադիտում',
    mergeRecipes: 'Բաղ. տողեր',
    mergeWillCombine: 'քն. միավորվում',
    mergeWillRepoint: 'տեղափոխ.',
    mergeSubstitutes: 'Փոխարինիչներ',
    mergeChoiceOptions: 'Ընտ. տարբ.',
    mergePurchaseLines: 'Գնման տողեր',
    mergeTransactions: 'Գործարքներ',
    mergeCombinedStock: 'Ընդհ. պաշար',
    mergeCombinedCost: 'Ընդհ. մի. արժ.',
    mergeUnitMismatch: 'Միավորները պետք է համընկնեն։',
    mergePrepBlocked: 'Prep բաղ. չեն միավորվում։',
    mergeConfirm: 'Միավորել',
    mergeSuccess: 'Միավորվեց։',
    recipes: 'Բաղադրատոմսեր',
    recipesUsing: 'Բաղադրատոմսեր — {{name}}',
    recipesUsingCaption: 'Բացեք բաղադրատոմսը խմբագրելու համար։',
    recipesNone: 'Ոչ մի բաղադրատոմսում չի օգտագործվում։',
  },
  recipes: {
    title: 'Բաղադրատոմսեր',
    description: 'Սահմանեք բաղադրատոմսեր, որոնք կապում են ընտրացանկի ապրանքները բաղադրիչներին։',
    createCard: 'Ստեղծել',
    addIngredient: 'Ավելացնել',
    newCardName: 'Նոր բաղ.',
    selectHint: 'Ձախ կողմից ընտրեք բաղ.',
    item: 'բաղ.',
    items: 'բաղ.',
    noCards: 'Բաղ. չկա',
    colIngredient: 'Բաղ.',
    colSubstitute: 'Փոխ. հնար.',
    colQty: 'Քն. / չ.',
    colCost: 'Արժ.',
    colPriceDelta: 'Գնի փոփ.',
    totalCost: 'Ընդամենը',
    costMissing: 'Բացակայում է միավորի արժեքը',
    noSubstitute: '—',
    substituteOptional: 'կամ.',
    substituteQtyPlaceholder: 'Փոխ. քն.',
    noItems: 'Բաղ. չկա',
    prepStock: 'պատր. պաշար',
    prepStockOnSale: 'Կրճատվում է վաճ. ժ.',
    batchEquivalent: 'Խմբ. համարժեք',
    legacyPrepSubRecipe: 'Հին ենթաբաղ. — օգտ. պատր. բաղ.',
    prepSubRecipeBlocked:
      'Պատր. բաղ. չեն կարող ենթաբաղ. լինել։ Ավելացրեք պատր. բաղադրիչը խմբ.ից հետո։',
    prepStandardBatch: 'Խմբ. արժ.',
    prepStandardUnit: 'Մի. արժ.',
    prepStoredCost: 'Պահված',
    prepCostMissing: 'Բացակայում է արժ.',
    prepCostSubRecipes: 'Ենթաբաղ. չեն հաշվ.— ավելացրեք հում բաղ.',
    recalcPrepCost: 'Վեր հաշ. ստանդ. արժ.',
  },
  sales: {
    title: 'Վաճառք',
    forcedOnly: 'Միայն հարկ. փակված',
    noOrders: 'Պատ. չկա',
    colNumber: '№',
    colTime: 'Ժամ',
    colStatus: 'Կ.',
    colTotal: 'Ընդ.',
    colForced: 'Հ.փ.',
    colNote: 'Ծ.',
  },
  receiptTemplate: {
    title: 'Կտրոնի ձևանմուշ',
    subtitle: 'Հարմարեցրեք կտրոնի տեսքը։',
    cafeName: 'Կաֆեի անուն',
    header: 'Վերնագիր',
    headerPlaceholder: 'Ապ. ցուցակից վեր լրաց. տողեր (ոչ պարտ.)',
    footer: 'Ստորագիր',
    footerPlaceholder: 'Ամփոփ. հաղ. ընդ. գծից ներքև',
    paperWidth: 'Թղթի լայնություն',
    printFontSize: 'Տառի չափ',
    fontNormal: 'Սովորական',
    fontLarge: 'Մեծ (կրկ. բարձր)',
    fontXlarge: 'Շատ մեծ (կրկ. լայն + բարձր)',
    printFontSizeHint: 'ESC/POS հրամաններ տhermal տպիչում։ Շատ մեծ — ավելի քիչ նիշ տողում։',
    printBold: 'Թավ տառ',
    printBoldHint: 'Թավ անուն, ապրանքներ և ընդամենը տպված կտրոնում։',
    preview: 'Նախ. տեսք',
    save: 'Պահել',
    saved: 'Պահվեց',
  },
  customerDisplay: {
    title: 'Հաճախորդի էկրան',
    subtitle: 'Կարգավորեք պланшետի էկրանը — բանнер և քարտային tip-եր։',
    enabled: 'Միացնել հաճ. էկրան',
    enabledHint: 'POS-ը բացում է http://<pos-ip>:9200 պլանшետում։',
    bannerTitle: 'Բանнерի վերնագիր',
    bannerMessage: 'Բանների հաղորդագրություն',
    bannerImageUrls: 'Բանների նկարներ (մինչև 4)',
    bannerImageUrlsHint: 'Վերբեռնեք կամ տեղադրեք URL. Սպասման էկրանին 5 վայրկյանը մեկ փոխվում է.',
    bannerImageUrlN: 'Նկար {{n}}',
    bannerImageUpload: 'Վերբեռնել նկար',
    bannerImageUploading: 'Վերբեռնում…',
    bannerImageClear: 'Հեռացնել',
    bannerImageUrlPlaceholder: 'Կամ տեղադրեք նկարի URL…',
    tipsSection: 'Քարտային tip',
    tipEnabled: 'Հարցնել tip քարտով վճ. ժամանակ',
    percentPresets: 'Տոկոսային preset-ներ (%)',
    smallOrderThreshold: 'Փոքր պատվeri շեմ (֏)',
    smallOrderThresholdHint: 'Այս գումարից ցածր — ֏ preset-ներ, ոչ տոկոս։',
    fixedPresets: 'Фиксированные preset-ы (֏)',
    tipTimeout: 'Tip timeout (վ)',
    tipTimeoutHint: 'Հաճ. ընտր. ժամանակ; դրամարկղ. կարող է բաց թողնել։',
    fiscalSection: 'HDM tip (фиск.)',
    fiscalHint: 'Պարտ. է tip-ը фиск. կտրոնում տպելու համար։',
    tipDepartmentId: 'Tip department ID',
    tipAdgCode: 'Tip ADG code',
    tipProductCode: 'Tip product code',
    tipProductName: 'Tip product name',
    save: 'Պահել',
    saved: 'Պահվեց',
    loyaltySection: 'Լոյալության գրանցում',
    loyaltySectionHint:
      'Սպասման էկրանին ցույց տալ «Միացեք հավելվածին» կոճակը։ Հյուրերը QR-ով կարող են գրանցվել։ Պահանջում է signup link Loyalty բաժնից։',
    loyaltyEnabled: 'Ցույց տալ լոյալության գրանցումը հաճ. էկրանին',
    loyaltySignupUrl: 'Signup link URL',
    loyaltySignupUrlHint:
      'Տեղադրեք հրապարակային join link-ը (Loyalty → copy link)։ QR-ը կոդավորում է այս URL-ը։',
    loyaltyUrlRequired: 'Signup link URL-ը պարտադիր է լոյալության ակցիայի համար։',
    loyaltyUrlRequiredHint: 'Ավելացրեք signup link — toggle-ը միացվում է URL-ից հետո։',
    loyaltyQuickLinks: 'Արագ լրացում signup link-երից',
    loyaltyHeadline: 'QR էկրանի վերնագիր',
    loyaltySubtext: 'QR էկրանի ենթատեքստ',
    loyaltyCtaLabel: 'Կոճակի տեքստ (idle)',
  },
  suppliers: {
    title: 'Մատակարարներ',
    subtitle: 'Կառավարեք մատակարարներին։ Իրավաբ. անունը ավտոմ. լրացվում է հաշիվ-ապ.-ից։',
    addSupplier: 'Ավել. մատ.',
    editSupplier: 'Խմբ. մատ.',
    noSuppliers: 'Մատ. չկա։',
    noMatch: 'Ոչ մի մատ. չի գտնվել։',
    nameRequired: 'Անունը պարտ. է։',
    tinRequired: 'ՀՎՀՀ-ն պարտ. է։',
    colName: 'Անուն',
    colLegalName: 'Իրավ. անուն',
    colTin: 'ՀՎՀՀ',
    colCategory: 'Կատ.',
    colContact: 'Կոնտ.',
    colPhone: 'Հեռ.',
    name: 'Մատ. անուն',
    namePlaceholder: 'օր.՝ Fresh Market',
    legalName: 'Իրավ. անուն',
    legalNameHint: 'հաշիվ-ապ.-ից',
    tin: 'ՀՎՀՀ',
    tinLocked: 'հնարավոր չէ փոփ.',
    category: 'Կատ.',
    contactPerson: 'Կոնտ. անձ',
    contactPersonPlaceholder: 'օր.՝ Արմեն Հ.',
    phone: 'Հեռ. համ.',
    cat_vegetables: 'Բանջ.',
    cat_wine: 'Գինի և ալկ.',
    cat_milk: 'Կաթ.',
    cat_drinks: 'Ըմպ.',
    cat_coffee: 'Սուրճ',
    cat_food: 'Սնունդ',
    cat_services: 'Ծառ.',
    cat_cleaning: 'Մաքր.',
    cat_packaging: 'Փաթ.',
    cat_other: 'Այլ',
  },
  floorPlan: {
    title: 'Հատակագիծ',
    subtitle: 'Դասավորեք սրահներն ու սեղանները։ POS-ը ցույց կտա հենց այս դասավորությունը։',
    addTable: 'Ավել. սեղան',
    addRoom: 'Ավել. սրահ',
    roomNamePrompt: 'Սրահի անունը (օր.՝ Տերասա)․',
    emptyRoom: 'Այս սրահում սեղաններ դեռ չկան — սեղմեք «+ Ավել. սեղան»։',
    selectHint: 'Ընտրեք սեղանը՝ խմբագրելու համար։ Քաշեք՝ դասավորելու համար։',
    tableName: 'Սեղանի անուն',
    seats: 'Տեղեր',
    shape: 'Ձև',
    shape_square: 'Քառակուսի',
    shape_round: 'Կլոր',
    shape_rect: 'Ուղղանկյուն',
    size: 'Չափ',
    room: 'Սրահ',
    active: 'Ակտիվ',
    deactivatedInstead: 'Սեղանն ունի պատվերների պատմություն, ուստի ապակտիվացվեց՝ ջնջվելու փոխարեն։',
  },
  rooms: {
    title: 'Սենյակներ',
    subtitle: 'Ամրագրվող սենյակներ և հյուրերի քանակ։ Ամեն սենյակ ունի իր iCal հղումը։',
    add: 'Ավել. սենյակ',
    edit: 'Խմբ. սենյակ',
    empty: 'Դեռ սենյակներ չկան — ավելացրեք առաջինը՝ ամրագրումներ ընդունելու համար։',
    nameRequired: 'Սենյակի անունը պարտադիր է։',
    priceRequired: 'Գիշերային գինը պետք է լինի զրոյից մեծ։',
    namePlaceholder: 'օր.՝ Սենյակ 1',
    colName: 'Անուն',
    colCapacity: 'Տեղեր',
    colPrice: 'Գին / գիշեր',
    colSort: 'Դաս.',
    colActive: 'Ակտ.',
    colFeed: 'iCal հղում',
    activeYes: 'Այո',
    activeNo: 'Ոչ',
    copyFeed: 'Պատճենել',
    copied: 'Պատճենվեց ✓',
    hdmTitle: 'HDM ֆիսկալ բաժին',
    hdmHint:
      'Բաժնի համարը, որն օգտագործվում է սենյակի ամրագրման ֆիսկալ կտրոն տպելիս։ Պետք է համընկնի HDM սարքի բաժնի հետ (ինչպես մենյուի «HDM dept»)։',
    hdmDept: 'HDM բաժին',
    hdmDeptTitle: 'HDM ֆիսկալ բաժնի համար (պետք է համընկնի սարքի հետ)',
    feedTitle: 'iCal հասանելիության արտահանում',
    feedHint:
      'Տեղադրեք սենյակի հղումը Airbnb-ում, Booking.com-ում կամ Expedia-ում («Import calendar»)։ Հղումը գաղտնի է — պահեք ինչպես գաղտնաբառ։',
    rotate: 'Թարմացնել հղումները',
    rotateConfirm: 'Նախկինում կիսված բոլոր հղումները կդադարեն աշխատել։ Շարունակե՞լ։',
    rotateYes: 'Այո, թարմացնել',
    widgetTitle: 'Ամրագրման վիջեթ',
    widgetHint:
      'Հանրային օրացույց, որտեղ հյուրերը կարող են ուղղակի ամրագրել։ Կիսվեք հղումով կամ տեղադրեք ձեր կայքում iframe կոդով։',
    widgetTitleLabel: 'Վիջեթի վերնագիր',
    widgetTitlePlaceholder: 'Լռելյայն՝ ձեր բիզնեսի անունը',
    widgetColorLabel: 'Կոճակի գույն',
    widgetColorHint: 'Օգտագործվում է Ամրագրել և Հաստատել կոճակների համար։',
    widgetOpen: 'Բացել վիջեթը',
    widgetCopyLink: 'Պատճենել հղումը',
    widgetCopyEmbed: 'Պատճենել embed կոդը',
    notifyEmailsTitle: 'Սրճարանին ծանուցել նոր հայտերի մասին',
    notifyEmails: 'Սրճարանի էլ. փոստերի ցանկ',
    notifyEmailsPlaceholder: 'cafe@example.com, manager@example.com',
    notifyEmailsHint:
      'Այս հասցեները նամակ կստանան, երբ հյուրը նոր ամրագրման հայտ ուղարկի վիջեթից։ Հյուրը դեռևս ստանում է իր հայտի նամակը։',
    photos: 'Լուսանկարներ',
    addPhoto: 'Ավելացնել լուսանկար',
    photosHint: 'Ցուցադրվում է ամրագրման վիջեթում։ JPEG, PNG կամ WebP, մինչև 4 ՄԲ։',
  },
  bookingWidget: {
    kicker: 'Ամրագրեք մնալը',
    subtitle: 'Սենյակների հասանելիություն',
    guests_one: '{{count}} հյուր',
    guests_other: '{{count}} հյուր',
    guestsLabel: 'Հյուրեր',
    perNight: '/ գիշեր',
    forStay: '{{count}} գիշերի համար',
    fullyBooked: 'Ամբողջովին զբաղված',
    noRooms: 'Դեռ հասանելի սենյակներ չկան։',
    loadError: 'Չհաջողվեց բեռնել տվյալները։ Փորձեք ավելի ուշ։',
    pickCheckin: 'Ընտրեք մուտքի օրը։',
    pickCheckout: 'Այժմ ընտրեք ելքի օրը։',
    nights_one: '{{count}} գիշեր',
    nights_other: '{{count}} գիշեր',
    guestName: 'Անուն ազգանուն',
    phone: 'Հեռախոսահամար',
    book: 'Ամրագրել',
    email: 'Էլ. փոստ',
    bookSuccessTitle: 'Հայտը ստացվել է',
    bookSuccess: 'Կտեղեկացնենք էլ. փոստով, երբ հյուրանոցը հաստատի կամ մերժի։ Սա դեռ հաստատված ամրագրում չէ։',
    colRoom: 'Սենյակ',
    bookConflict: 'Ցավոք, այդ օրերը հենց նոր զբաղեցվեցին։ Ընտրեք այլ օրեր։',
    bookError: 'Ինչ-որ բան սխալ գնաց։ Փորձեք կրկին։',
    checkIn: 'Մուտք',
    checkOut: 'Ելք',
    addDate: 'Ավելացնել օր',
    resetDates: 'Վերականգնել',
    chooseDates: 'Ընտրել օրեր',
    reserve: 'Ամրագրել',
    unavailable: 'Անհասանելի',
    tooSmall: 'Այս սենյակը չի տեղավորում {{count}} հյուր։',
    tooSmallBadge: 'Քիչ տեղ',
    availableCount: '{{count}} սենյակ հասանելի է',
    unavailableCount: '{{count}} անհասանելի',
    noMatch: 'Այդ օրերին ազատ սենյակ չկա։ Փորձեք այլ ամսաթվեր։',
    browseHint: 'Ընտրեք օրերը՝ տեսնելու ազատ սենյակները, կամ դիտեք ցանկը։',
    stayTotal: 'Ընդամենը',
    bookAnother: 'Ամրագրել ևս մեկ մնալ',
    back: 'Բոլոր սենյակները',
    guestDetails: 'Ձեր տվյալները',
    payOnSite: 'Վճարումը՝ տեղում։ Հիմա գումար չի գանձվում։',
    confirmBooking: 'Ուղարկել հայտը',
    sleeps: 'Մինչև {{count}} հյուր',
    prevPhoto: 'Նախորդ լուսանկար',
    nextPhoto: 'Հաջորդ լուսանկար',
    prevMonth: 'Նախորդ ամիս',
    nextMonth: 'Հաջորդ ամիս',
    fewerGuests: 'Պակաս հյուր',
    moreGuests: 'Ավել հյուր',
    datesNoLongerAvailable: 'Այս սենյակն այլևս ազատ չէ նոր օրերին։ Ընտրեք այլ սենյակ։',
    done: 'Պատրաստ է',
    viewPhotos: 'Դիտել լուսանկարները',
    closePhotos: 'Փակել',
    photoOf: '{{current}} / {{total}}',
  },
  bookings: {
    title: 'Ամրագրումներ',
    subtitle: 'Սենյակների զբաղվածության օրացույց։ Սեղմեք ազատ օրվա վրա՝ ամրագրելու համար։',
    subtitleTable: 'Ընտրված ամսվա բոլոր ամրագրումները։ Սեղմեք տողի վրա՝ դիտելու կամ խմբագրելու համար։',
    viewCalendar: 'Օրացույց',
    viewTable: 'Աղյուսակ',
    add: 'Նոր ամրագրում',
    edit: 'Ամրագրում',
    today: 'Այսօր',
    colRoom: 'Սենյակ',
    colNights: 'Գիշերներ',
    colStatus: 'Կարգավիճակ',
    statusConfirmed: 'Հաստատված',
    statusPending: 'Սպասում',
    statusRejected: 'Մերժված',
    guestEmail: 'Էլ. փոստ',
    emailInvalid: 'Մուտքագրեք վավեր էլ. փոստ։',
    requestBanner: 'Կայքից հայտ — օրերը ազատ են մնում մինչև հաստատելը։',
    confirmRequest: 'Հաստատել հայտը',
    rejectRequest: 'Մերժել',
    confirmRequestAsk: 'Հաստատե՞լ այս հայտը։',
    rejectRequestAsk: 'Մերժե՞լ այս հայտը։',
    confirmRequestYes: 'Այո, հաստատել',
    rejectRequestYes: 'Այո, մերժել',
    reviewError: 'Հայտը չհաջողվեց թարմացնել։ Փորձեք կրկին։',
    noBookings: 'Այս ամիս ամրագրումներ չկան։',
    noRooms: 'Ակտիվ սենյակներ չկան — նախ ավելացրեք սենյակներ (Կառավարում → Ամրագրումներ)։',
    clickToBook: 'Սեղմեք՝ ամրագրելու համար',
    guestName: 'Հյուրի անուն',
    phone: 'Հեռախոս',
    checkIn: 'Մուտք',
    checkOut: 'Ելք',
    nights: '{{count}} գիշեր',
    adults: 'Մեծահասակ',
    kids: 'Երեխա',
    sourceLabel: 'Աղբյուր',
    note: 'Նշում',
    roomRequired: 'Ընտրեք սենյակ։',
    nameRequired: 'Հյուրի անունը պարտադիր է։',
    phoneRequired: 'Հեռախոսը պարտադիր է։',
    datesInvalid: 'Ելքը պետք է լինի մուտքից հետո։',
    adultsRequired: 'Առնվազն մեկ մեծահասակ։',
    overCapacity: 'Չափազանց շատ հյուր այս սենյակի համար (տեղեր՝ {{capacity}})։',
    conflict: 'Այս սենյակն արդեն ամրագրված է այդ օրերի համար։',
    cancelBooking: 'Չեղարկել ամրագրումը',
    cancelYes: 'Այո, չեղարկել',
    cancelled: 'Չեղարկված',
    close: 'Փակել',
    amount: 'Գումար (AMD)',
    amountHint: 'Լռելյայն՝ սենյակի գին × գիշերներ, կարող եք փոխել։',
    amountRequired: 'Գումարը պետք է լինի զրոյից մեծ։',
    paymentOption: 'Վճարման տարբերակ',
    paymentOptionRequired: 'Վճարման տարբերակը պարտադիր է։',
    paymentOptionValue: {
      on_site: 'Տեղում',
      online: 'Առցանց',
      free: 'Անվճար',
    },
    freeHint: 'Անվճար մնալ։ Սենյակը զբաղված է, գումար չի գանձվում։',
    freeAfterPayment: 'Այս մնալն արդեն վճարված է։ Նախ վերադարձրեք, հետո նշեք անվճար։',
    paymentStatusLabel: 'Վճարման կարգավիճակ',
    paymentStatus: {
      unpaid: 'Չվճարված',
      pending: 'Սպասում',
      paid: 'Վճարված',
      failed: 'Ձախողված',
    },
    tender: {
      cash: 'Կանխիկ',
      card: 'Քարտ',
    },
    fiscalTitle: 'Ֆիսկալ կտրոն',
    fiscalNumber: 'Ֆիսկալ #',
    fiscalTotal: 'Ֆիսկալ գումար',
    fiscalCrn: 'CRN',
    fiscalTin: 'ՀՎՀՀ',
    fiscalTime: 'Տպման ժամ',
    fiscalNone: 'Ֆիսկալ կտրոն դեռ չկա։',
    refund: 'Վերադարձ',
    refundBooking: 'Ամրագրումը վերադարձ',
    refundTitle: 'Ամրագրումը վերադարձ',
    refundConfirmCancel: 'Ֆիսկալ վերադարձի կտրոնից հետո ամրագրումը կչեղարկվի։',
    refundReturnCash: 'Վերադարձ {{amount}} կանխիկ',
    refundReturnCard: 'Վերադարձ {{amount}} քարտով',
    refunding: 'Վերադարձի կտրոն…',
    refundSuccess: 'Վերադարձված — ֆիսկալ #{{fiscal}}։ Ամրագրումը չեղարկված է։',
    refundFailed: 'Վերադարձը ձախողվեց՝ {{msg}}',
    refundFiscalTitle: 'Վերադարձի կտրոն',
    refundOnPosOnly:
      'Վճարված է ֆիսկալ կտրոնով — վերադարձը կատարեք POS-ից՝ վերադարձի կտրոն տպելու և ամրագրումը չեղարկելու համար։',
    payConfirmTitle: 'Հաստատել վճարումը',
    payConfirmCash: 'Գանձե՞լ {{amount}} կանխիկ և տպել ֆիսկալ կտրոն։',
    payConfirmCard: 'Գանձե՞լ {{amount}} քարտով և տպել ֆիսկալ կտրոն։',
    payConfirmYes: 'Հաստատել վճարումը',
    source: {
      airbnb: 'Airbnb',
      expedia: 'Expedia',
      booking: 'Booking.com',
      web: 'Կայք',
      instagram: 'Instagram',
      on_site: 'Տեղում',
      other: 'Այլ',
    },
  },
  bookingInsights: {
    title: 'Ամրագրումների վերլուծություն',
    subtitle: 'Զբաղվածություն, ADR, RevPAR և ալիքների արդյունավետություն ընտրված ժամանակահատվածում։',
    period_7d: '7 օր',
    period_30d: '30 օր',
    period_90d: '90 օր',
    kpiOccupancy: 'Զբաղվածություն',
    kpiAdr: 'ADR',
    kpiRevpar: 'RevPAR',
    kpiRevenue: 'Վճարված եկամուտ',
    kpiStays: 'Հաստատված մնալ',
    kpiAvgLos: 'Միջ. մնալու տև.',
    kpiCollection: 'Հավաքման տոկոս',
    kpiOutstanding: 'Պարտք',
    kpiComplimentary: 'Անվճար',
    kpiCancelRate: 'Չեղարկման տոկոս',
    compHint: 'Զբաղված գիշերներ՝ եկամտից և ADR-ից դուրս',
    adrHint: 'Վճարված ÷ վճարված գիշերներ',
    revparHint: 'Վճարված ÷ հասանելի գիշերներ',
    revenueHint: 'Հավաքված ժամանակահատվածում',
    staysHint: 'Չչեղարկված մուտքեր',
    cancelledCount: '{{count}} չեղարկված',
    roomNights: 'գիշեր-սենյակ',
    nightsCount: '{{count}} գիշեր',
    nightsShort: 'գիշ.',
    staysShort: 'մնալ',
    trendTitle: 'Օրական հավաքված եկամուտ',
    channelMixTitle: 'Եկամուտն ըստ ալիքի',
    roomPerformanceTitle: 'Սենյակների արդյունավետություն',
    paymentMixTitle: 'Վճարման կարգավիճակ',
    bySource: 'Աղբյուրով',
    byPaymentOption: 'Վճարման տարբերակով',
    byRoom: 'Սենյակով',
    recentPaid: 'Վերջին վճարումներ',
    colSource: 'Աղբյուր',
    colOption: 'Վճարման տարբերակ',
    colRoom: 'Սենյակ',
    colBookings: 'Ամրագրումներ',
    colPaid: 'Վճարված',
    colRevenue: 'Եկամուտ',
    colGuest: 'Հյուր',
    colDates: 'Ամսաթվեր',
    colAmount: 'Գումար',
    colFiscal: 'Ֆիսկալ #',
    colTender: 'Վճարում',
    noData: 'Այս ժամանակահատվածում մնալ չկա։',
    noPaidBySource: 'Ալիքով վճարված եկամուտ դեռ չկա։',
    noPaidYet: 'Վճարված ամրագրումներ չկան։',
  },
  staffComps: {
    title: 'Անվճ. պատվ.',
    subtitle: 'Անվճար force-close-ներ և աշխատակցի սնունդ (waste)։',
    period_7d: '7 օր',
    period_30d: '30 օր',
    period_90d: '90 օր',
    kpiOrders: 'Պատվերներ',
    kpiTotal: 'Ընդամենը',
    kpiAvg: 'Միջին',
    byRecipient: 'Ստացողով',
    byType: 'Տեսակով',
    colRecipient: 'Ստացող',
    colType: 'Տեսակ',
    colOrders: 'Պատվ.',
    colTotal: 'Ընդ.',
    noData: 'Տվյալ չկա։',
    unassigned: 'Չնշ.',
    unknownRecipient: 'Անհ.',
  },
  compTypes: {
    staff_meal: 'Աշխ. սն.',
    owner: 'Սեփ.',
    guest: 'Հյուր',
    training: 'Ուս.',
    marketing: 'Մարք.',
    waste: 'Թափ.',
    other: 'Այլ',
  },
  demo: {
    tryDemo: 'Փորձել դեմո',
    or: 'կամ',
    signInHint: 'Համօգտագործվող դեմո — տվյալները վերականգնվում են գիշերը։',
    banner: 'Դեմո միջավայր — նմուշ տվյալները վերականգնվում են գիշերը։',
  },
};

const enAll = deepMerge(en as unknown as Record<string, unknown>, extrasEn);
const hyAll = deepMerge(hy as unknown as Record<string, unknown>, extrasHy);
const ruAll = deepMerge(
  deepMerge(enAll, ruCore as unknown as Record<string, unknown>),
  extrasRu as unknown as Record<string, unknown>,
);

void i18n.use(initReactI18next).init({
  lng: resolveAppLang(localStorage.getItem(LANG_STORAGE_KEY)),
  fallbackLng: 'en',
  supportedLngs: ['en', 'hy', 'ru'],
  resources: {
    en: { translation: enAll },
    hy: { translation: hyAll },
    ru: { translation: ruAll },
  },
  interpolation: { escapeValue: false },
});

export default i18n;
